import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import api from '../utils/api';
import { AlertTriangle, TrendingUp, TrendingDown, Plus, Trash2 } from 'lucide-react';

const ReconciliationPopup = ({ show, onClose, companies }) => {
  const [reconciliationStatus, setReconciliationStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompanyForEntry, setSelectedCompanyForEntry] = useState('');
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [newEntry, setNewEntry] = useState({
    company_id: '',
    bank_account_id: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    entry_type: 'expense',
    amount: '',
    description: '',
    entry_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (show) {
      fetchReconciliationStatus();
    }
  }, [show]);

  const fetchReconciliationStatus = async () => {
    setLoading(true);
    try {
      const statuses = [];
      for (const company of companies) {
        const response = await api.get(`/reconciliation-status/${company.id}`);
        if (response.data.has_discrepancies) {
          statuses.push({
            company: company,
            ...response.data
          });
        }
      }
      setReconciliationStatus(statuses);
    } catch (error) {
      console.error('Error fetching reconciliation status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (companyId) => {
    // Get bank accounts for company
    try {
      const bankAccountsResponse = await api.get('/bank-accounts');
      const companyAccounts = bankAccountsResponse.data.filter(acc => acc.company_id === companyId);
      
      if (companyAccounts.length === 0) {
        alert('Questa azienda non ha conti bancari. Creane uno prima.');
        return;
      }
      
      setNewEntry({
        ...newEntry,
        company_id: companyId,
        bank_account_id: companyAccounts[0].id
      });
      setSelectedCompanyForEntry(companyId);
      setShowEntryDialog(true);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    try {
      await api.post('/reconciliation-entries', {
        ...newEntry,
        amount: parseFloat(newEntry.amount),
        entry_date: new Date(newEntry.entry_date).toISOString()
      });
      
      setShowEntryDialog(false);
      setNewEntry({
        company_id: '',
        bank_account_id: '',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        entry_type: 'expense',
        amount: '',
        description: '',
        entry_date: new Date().toISOString().split('T')[0]
      });
      
      // Refresh status
      fetchReconciliationStatus();
      alert('Voce di riconciliazione salvata con successo!');
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Errore nel salvataggio della voce');
    }
  };

  if (!show) return null;

  return (
    <>
      <Dialog open={show} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
              Riconciliazione Bancaria - Promemoria
            </DialogTitle>
          </DialogHeader>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto"></div>
            </div>
          ) : reconciliationStatus.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-green-600 mb-2">✅ Tutto riconciliato!</div>
              <p className="text-slate-600">Non ci sono discrepanze da gestire questo mese.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-slate-700">
                Hai delle discrepanze tra i saldi bancari reali e quelli calcolati. 
                Inserisci le voci mancanti per riconciliare i conti.
              </p>
              
              {reconciliationStatus.map((status, index) => (
                <div key={index} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-semibold text-slate-900">
                        {status.company.name}
                      </div>
                      <div className="text-sm text-slate-600">
                        {status.year} - Mese {status.month}
                      </div>
                    </div>
                    <Badge className="bg-orange-100 text-orange-800">
                      Discrepanza: €{Math.abs(status.discrepancy_amount).toFixed(2)}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                    <div>
                      <div className="text-slate-600">Saldo Reale:</div>
                      <div className="font-semibold text-slate-900">
                        €{status.saldo_reale.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-600">Saldo Calcolato:</div>
                      <div className="font-semibold text-slate-900">
                        €{status.saldo_calcolato.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => handleAddEntry(status.company.id)}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Aggiungi Voce di Riconciliazione
                  </Button>
                </div>
              ))}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={onClose}>
                  Chiudi
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    window.location.href = '/bank-balances';
                  }}
                  className="bg-blue-900 hover:bg-blue-800"
                >
                  Vai a Saldi Banche
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Entry Dialog */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuova Voce di Riconciliazione</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEntry} className="space-y-4">
            <div>
              <Label>Tipo *</Label>
              <Select
                value={newEntry.entry_type}
                onValueChange={(value) => setNewEntry({ ...newEntry, entry_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">
                    <span className="flex items-center gap-2">
                      <TrendingDown className="h-4 w-4 text-red-600" />
                      Uscita Non Registrata
                    </span>
                  </SelectItem>
                  <SelectItem value="income">
                    <span className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      Entrata Non Registrata
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Importo * (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={newEntry.amount}
                onChange={(e) => setNewEntry({ ...newEntry, amount: e.target.value })}
                required
                placeholder="0.00"
              />
            </div>
            
            <div>
              <Label>Descrizione *</Label>
              <Input
                value={newEntry.description}
                onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                required
                placeholder="es. Commissione bancaria, Bonifico in entrata, ecc."
              />
            </div>
            
            <div>
              <Label>Data Transazione *</Label>
              <Input
                type="date"
                value={newEntry.entry_date}
                onChange={(e) => setNewEntry({ ...newEntry, entry_date: e.target.value })}
                required
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEntryDialog(false)}>
                Annulla
              </Button>
              <Button type="submit" className="bg-blue-900 hover:bg-blue-800">
                Salva
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReconciliationPopup;
